import React, { useState } from "react"
import { getServicePath } from "@core-frontend/Core/Frontend"
import { Http } from "@core-ts/Http"

interface FormData {
    firstName: string
    lastName: string
    email: string
    phone: string
    company: string
    jobTitle: string
    city: string
    message: string
}

interface ContactFormProps {
    onSubmit?: (data: FormData) => void
}

export function ContactForm({ onSubmit }: ContactFormProps): React.JSX.Element {
    const [formData, setFormData] = useState<FormData>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        jobTitle: "",
        city: "",
        message: "",
    })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (onSubmit) {
            onSubmit(formData)
        }

        const sendEmailPath = getServicePath("ecstore") + "sendEmail"
        await Http.postJson(sendEmailPath, formData)

        console.log("Form submitted:", formData)
    }

    const labelStyle: React.CSSProperties = {
        display: "block",
        marginBottom: "8px",
        fontSize: "14px",
        fontWeight: "400",
        color: "#333",
    }

    const inputStyle: React.CSSProperties = {
        width: "100%",
        padding: "12px 16px",
        border: "1px solid #ddd",
        borderRadius: "6px",
        fontSize: "14px",
        backgroundColor: "#fff",
        outline: "none",
        transition: "border-color 0.2s ease",
        boxSizing: "border-box",
    }

    return (
        <section id="contact">
            <style>{`
                .contact-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-bottom: 24px;
                }

                @media (max-width: 768px) {
                    .contact-grid {
                        grid-template-columns: 1fr;
                    }

                    .contact-wrapper {
                        padding: 24px 16px !important;
                        border-radius: 0 !important;
                        margin: 0 !important;
                    }
                }
            `}</style>

            <div
                className="contact-wrapper"
                style={{
                    maxWidth: "700px",
                    margin: "0 auto",
                    padding: "30px 30px",
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                }}
            >
                <h1
                    style={{
                        textAlign: "center",
                        marginBottom: "30px",
                        fontSize: "26px",
                        fontWeight: "600",
                        color: "#333",
                        letterSpacing: "-0.3px",
                    }}
                >
                    Kontakta Oss
                </h1>

                <p
                    style={{
                        textAlign: "center",
                        maxWidth: "700px",
                        fontSize: "15px",
                    }}
                >
                    Fyll i formuläret nedan så hör vi av oss till er så snart vi kan. Välj om du vill fylla i formuläret
                    som privatperson eller som BRF/Företag.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="contact-grid">
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Förnamn</label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                                required
                            />
                        </div>
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Efternamn</label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                                required
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: "24px" }}>
                        {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                        <label style={labelStyle}>E-post</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            style={inputStyle}
                            // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                            onFocus={e => (e.target.style.borderColor = "#999")}
                            // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                            onBlur={e => (e.target.style.borderColor = "#ddd")}
                            required
                        />
                    </div>

                    <div className="contact-grid">
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Telefon</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                            />
                        </div>
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Företag</label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                            />
                        </div>
                    </div>

                    <div className="contact-grid">
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Befattning</label>
                            <input
                                type="text"
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                            />
                        </div>
                        <div>
                            {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                            <label style={labelStyle}>Ort</label>
                            <input
                                type="text"
                                name="city"
                                onChange={handleInputChange}
                                style={inputStyle}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onFocus={e => (e.target.style.borderColor = "#999")}
                                // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                                onBlur={e => (e.target.style.borderColor = "#ddd")}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: "30px" }}>
                        {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                        <label style={labelStyle}>Meddelande</label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows={4}
                            style={{
                                ...inputStyle,
                                resize: "vertical",
                                minHeight: "80px",
                            }}
                            // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                            onFocus={e => (e.target.style.borderColor = "#999")}
                            // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                            onBlur={e => (e.target.style.borderColor = "#ddd")}
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        style={{
                            width: "100%",
                            padding: "14px",
                            backgroundColor: "#F485C7",
                            color: "black",
                            border: "none",
                            borderRadius: "25px",
                            fontSize: "16px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                        }}
                        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f770c1")}
                        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#F485C7")}
                        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                        onFocus={e => (e.currentTarget.style.backgroundColor = "#f770c1")}
                        // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                        onBlur={e => (e.currentTarget.style.backgroundColor = "#F485C7")}
                    >
                        Skicka
                    </button>
                </form>
            </div>
        </section>
    )
}